import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import i18n from "i18n"
import PropTypes from "prop-types"
import { getInvoiceEditUrl } from "../utils/invoiceUtils"
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { useDeleteInvoice, useCancelInvoice } from "../hooks/useInvoices"
import usePdfDownload from "../hooks/usePdfDownload"
import { useAuthContext } from "context/auth/authContext"
import { useSnackbar } from "notistack"

const getCancelErrorMessage = error => {
  const reason = error?.response?.data?.reason
  const key = `invoices.cancelReasons.${reason}`
  return reason && i18n.exists(key)
    ? i18n.t(key)
    : i18n.t("invoices.cancelError")
}

const ActionsMenu = ({ row, projectId }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const navigate = useNavigate()
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)

  const { user, organizationFields } = useAuthContext()
  const organizationId = user?.["custom:organizationId"]
  const useInvopop = !!organizationFields?.useInvopop
  const deleteInvoice = useDeleteInvoice()
  const cancelInvoice = useCancelInvoice()
  const { enqueueSnackbar } = useSnackbar()
  const { downloadPdf } = usePdfDownload(row)

  const handleEditClick = () => {
    const url = getInvoiceEditUrl(row.invoiceId, projectId)
    if (url) {
      navigate(url)
    }
  }

  const [submenuAnchor, setSubmenuAnchor] = useState(null)
  const submenuOpen = Boolean(submenuAnchor)

  const handleSubmenuOpen = event => {
    setSubmenuAnchor(event.currentTarget)
  }

  const handleSubmenuClose = () => {
    setSubmenuAnchor(null)
    handleMenuClose()
  }

  const handleCorrectionClick = correctionType => () => {
    if (!row.invoiceId) return
    const params = new URLSearchParams({
      correctsInvoice: row.invoiceId,
      correctionType,
    })
    if (projectId) params.set("projectId", projectId)
    navigate(`/invoice/new?${params.toString()}`)
    handleSubmenuClose()
  }

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true)
    handleMenuClose()
  }

  const handleConfirmDelete = () => {
    setConfirmDeleteOpen(false)
    if (organizationId && row.invoiceId) {
      deleteInvoice.mutate(
        { organizationId, invoiceId: row.invoiceId },
        {
          onError: () => {
            enqueueSnackbar(i18n.t("invoices.deleteError"), {
              variant: "error",
            })
          },
        },
      )
    }
  }

  const handlePdfDownload = () => {
    downloadPdf(handleMenuClose)
  }

  const canVoidWithCreditNote =
    row.issued &&
    !row.cancelledAt &&
    !row.correctedBy &&
    row.rectificationStatus !== "fully_rectified" &&
    (row.invoiceType === "standard" || !row.invoiceType)

  // El backend rechaza anular si hay notas emitidas, y esas notas tampoco se pueden deshacer:
  // sin avisar acá, el usuario recibe un error que no tiene forma de resolver.
  const hasIssuedNotes = (row.corrections || []).some(
    c => c.issued && ["credit_note", "debit_note"].includes(String(c.type)),
  )
  const issuedNotesReason = i18n.t("invoices.cannotVoidWithIssuedNotes")
  const voidDisabledReason = hasIssuedNotes ? issuedNotesReason : null

  const canShowCorrectiveSubmenu =
    row.issued &&
    !row.correctedBy &&
    !row.cancelledAt &&
    row.rectificationStatus !== "fully_rectified" &&
    (row.invoiceType === "standard" || !row.invoiceType)

  // Desactivado a propósito, no es un olvido: con Invopop la anulación se hace por nota de
  // crédito. El backend expone cancel_invopop_invoice, pero se decidió no ofrecerlo, así que
  // este MenuItem no se renderiza nunca. Sin Invopop la anulación directa sí se usa.
  const canCancelElectronic = false

  const handleVoidWithCreditNote = () => {
    if (!row.invoiceId) return
    if (!useInvopop) {
      setConfirmCancelOpen(true)
      handleMenuClose()
      return
    }
    const params = new URLSearchParams({
      correctsInvoice: row.invoiceId,
      correctionType: "credit",
      voidTotal: "true",
    })
    if (projectId) params.set("projectId", projectId)
    navigate(`/invoice/new?${params.toString()}`)
    handleMenuClose()
  }

  const handleConfirmCancel = () => {
    setConfirmCancelOpen(false)
    if (organizationId && row.invoiceId) {
      cancelInvoice.mutate(
        { organizationId, invoiceId: row.invoiceId },
        {
          onError: error => {
            enqueueSnackbar(getCancelErrorMessage(error), { variant: "error" })
          },
        },
      )
    }
  }

  return (
    <>
      <IconButton
        size="small"
        aria-label="more"
        onClick={handleMenuOpen}
        data-testid={
          row?.invoiceId
            ? `invoices-row-actions-trigger-${row.invoiceId}`
            : "invoices-row-actions-trigger"
        }
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { "data-testid": "invoices-row-actions-menu" } }}
      >
        <MenuItem
          onClick={handleEditClick}
          data-testid="invoices-row-action-edit"
        >
          {i18n.t("invoices.editInvoice")}
        </MenuItem>
        {canShowCorrectiveSubmenu && (
          <MenuItem
            onClick={handleSubmenuOpen}
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            {i18n.t("invoices.createCorrectiveSubmenu")}
            <span style={{ marginLeft: 16, opacity: 0.6 }}>▸</span>
          </MenuItem>
        )}
        <MenuItem
          onClick={handlePdfDownload}
          data-testid="download-invoice-pdf-menu-item"
        >
          {i18n.t("invoices.downloadPdf")}
        </MenuItem>
        {canVoidWithCreditNote &&
          (voidDisabledReason ? (
            <Tooltip title={voidDisabledReason} placement="left">
              <span>
                <MenuItem disabled sx={{ opacity: 0.5 }}>
                  {i18n.t("invoices.voidInvoice")}
                </MenuItem>
              </span>
            </Tooltip>
          ) : (
            <MenuItem
              onClick={handleVoidWithCreditNote}
              sx={{ color: "error.main" }}
            >
              {i18n.t("invoices.voidInvoice")}
            </MenuItem>
          ))}
        {canCancelElectronic && (
          <MenuItem onClick={() => {}} sx={{ color: "error.main" }}>
            {i18n.t("invoices.cancelElectronic")}
          </MenuItem>
        )}
        {!row.issued && (
          <MenuItem
            onClick={handleDeleteClick}
            data-testid="invoices-row-action-delete"
          >
            {i18n.t("invoices.deleteInvoice")}
          </MenuItem>
        )}
      </Menu>
      <Menu
        anchorEl={submenuAnchor}
        open={submenuOpen}
        onClose={handleSubmenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleCorrectionClick("replace")}>
          {i18n.t("invoices.createReplacement")}
        </MenuItem>
        <MenuItem onClick={handleCorrectionClick("credit")}>
          {i18n.t("invoices.createCreditNote")}
        </MenuItem>
        <MenuItem onClick={handleCorrectionClick("debit")}>
          {i18n.t("invoices.createDebitNote")}
        </MenuItem>
      </Menu>
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        slotProps={{ paper: { "data-testid": "invoices-delete-modal" } }}
      >
        <DialogTitle>{i18n.t("invoices.deleteInvoice")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("invoices.deleteInvoiceConfirmation")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            color="secondary"
            data-testid="invoices-delete-cancel-button"
          >
            {i18n.t("invoices.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            data-testid="invoices-delete-confirm-button"
          >
            {i18n.t("buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
      >
        <DialogTitle>{i18n.t("invoices.cancelInvoice")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("invoices.cancelInvoiceConfirmation")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmCancelOpen(false)} color="secondary">
            {i18n.t("invoices.cancel")}
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
          >
            {i18n.t("invoices.cancelInvoice")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

ActionsMenu.propTypes = {
  row: PropTypes.object.isRequired,
  projectId: PropTypes.string,
}

export default ActionsMenu
